import React, { useCallback, useEffect, useState } from 'react';
import Card from '@material-ui/core/Card';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { get } from 'lodash';
import createLoadingSelector from '../../../selectors/loading';
import { CREATE_RX_FORM_PREFIX, createRxFormAction } from '../../../reducers/rxForms';
import OrganizationInfo from '../../../components/OrganizationInfo';
import LoadAvailableLocations from '../../../components/LoadAvailableLocations';
import LoadingIndicator from '../../../components/LoadingIndicator';
import useOrganizationPermissions from '../../../hooks/useOrganizationPermissions';
import OrganizationsApiService from '../../../services/api/organizations';
import Form from '../../../components/RxForm/Form';
import LoadingButton from '../../../components/LoadingButton';
import hasPermission from '../../../selectors/hasPermission';
import { fetchFormBuilderAction } from '../../../reducers/formBuilder';
import { CONTROL_EXISTING } from '../../Account/components/RxFormCustomization/FormBuilder/helpers';
import { RX_FORM_TYPES } from '../../../components/RxForm/constants';
import {
  RETAINER_ATTRIBUTES,
  RETAINER_CUT_TYPE_OPTIONS
} from '../../../components/RxForm/Retainer/constants';
import createRetainerNativeFields from '../../../components/RxForm/Retainer/nativeFields';
import { createRetainerValidationSchema } from '../../../components/RxForm/Retainer/validation';
import SavingProcess from '../SavingProcess';
import useStartCaseOrganization from '../hooks/useStartCaseOrganization';
import withOrganizationPricing from '../../../hooks/withOrganizationPricing';
import { ARCHES, SCAN_SUBMISSION_METHODS } from '../../../components/RxForm/IDB/constants';

const useStyles = makeStyles(() => ({
  form: {
    width: '70%',
    marginLeft: 'auto',
    marginRight: 'auto'
  }
}));

const AddRetainerRxForm = ({ match: { params } }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const organizationId = parseInt(params.organizationId, 10);
  const isManagement = useSelector(state => state.auth.isManagement);
  const { isLoading: isLoadingOrganizationPermissions } = useOrganizationPermissions({
    organizationId
  });
  const currentUser = useSelector(state => state.auth.user);
  const organization = useStartCaseOrganization(organizationId);
  const [preference, setPreference] = useState();
  const [saving, setSaving] = useState(null);
  const canSelectDoctor = useSelector(state =>
    hasPermission(state, {
      permissions: [
        'rx-forms.manage',
        'rx-forms.select-doctor',
        'organization.rx-forms.select-doctor'
      ],
      organizationId
    })
  );
  const isCreatingRxForm = useSelector(state =>
    createLoadingSelector([CREATE_RX_FORM_PREFIX])(state)
  );
  const formBuilderForm = useSelector(state => state.formBuilder.form);
  const formStructure = useSelector(state => state.formBuilder.layout);
  const newControls = Object.values(get(formBuilderForm, 'controls', {})).filter(
    control => control.system_type !== CONTROL_EXISTING
  );

  useEffect(() => {
    dispatch(
      fetchFormBuilderAction({
        organization_id: organizationId,
        type: RX_FORM_TYPES.RETAINER
      })
    );
  }, [organizationId, dispatch]);

  useEffect(() => {
    const organizationApiService = new OrganizationsApiService();
    organizationApiService.getRxFormPreference(organizationId).then(({ data }) => {
      setPreference(data);
    });
  }, [organizationId]);

  const navigate = () => {
    history.push(isManagement ? '/patients-page' : '/patients');
  };

  const createRxForm = useCallback(
    additionalValues => {
      return dispatch(
        createRxFormAction({
          ...saving,
          ...additionalValues
        })
      );
    },
    [saving, dispatch]
  );

  const handleSubmit = (values, { setSubmitting }) => {
    setSaving({
      ...values,
      organization_id: organization.id,
      type: RX_FORM_TYPES.RETAINER
    });
    setSubmitting(false);
  };

  if (!organization || !preference || !formBuilderForm || isLoadingOrganizationPermissions) {
    return <LoadingIndicator />;
  }

  const fieldsToInclude = Object.values(get(formBuilderForm, 'controls', {})).map(
    control => control.system_name
  );

  const rxForm = {
    [RETAINER_ATTRIBUTES.FIRST_NAME]: '',
    [RETAINER_ATTRIBUTES.LAST_NAME]: '',
    [RETAINER_ATTRIBUTES.CASE_NUMBER_TEXT]: '',
    [RETAINER_ATTRIBUTES.DOCTOR]: canSelectDoctor
      ? preference.default_doctor_id
      : currentUser && currentUser.id,
    [RETAINER_ATTRIBUTES.ARCH_TYPE]: ARCHES.BOTH,
    [RETAINER_ATTRIBUTES.RETAINER_CUT_TYPE]: RETAINER_CUT_TYPE_OPTIONS[1].id,
    [RETAINER_ATTRIBUTES.SCAN_SUBMISSION_METHOD]: SCAN_SUBMISSION_METHODS.UPLOAD,
    [RETAINER_ATTRIBUTES.SCANNER]: '1',
    [RETAINER_ATTRIBUTES.CUSTOM_SCANNER_NAME]: '',
    [RETAINER_ATTRIBUTES.STL_FILES]: [],
    [RETAINER_ATTRIBUTES.INTRAORAL_PHOTOS]: [],
    [RETAINER_ATTRIBUTES.SHIPPING_LOCATION]: preference.shipping_location_id,
    [RETAINER_ATTRIBUTES.SHIPPING_METHOD]: null,
    form_builder_layout_id: get(formBuilderForm, 'form_builder_layout_id', '')
  };

  const children = pricing => (
    <Card className={classes.form}>
      <LoadAvailableLocations organization={organization}>
        {availableLocations => (
          <Form
            organization={organization}
            availableLocations={availableLocations}
            title="Start A Case - Retainers"
            description={<OrganizationInfo organization={organization} />}
            rxForm={rxForm}
            formStructure={formStructure}
            newControls={newControls}
            only={fieldsToInclude}
            nativeFields={createRetainerNativeFields}
            createValidationSchema={createRetainerValidationSchema}
            onSubmit={handleSubmit}
            preference={preference}
            isLoading={isCreatingRxForm}
            hiddenFields={[]}
            pricing={pricing}
            actions={({ dirty, isValid, submitForm, status }) => (
              <LoadingButton
                type="submit"
                variant="contained"
                color="primary"
                loading={!!saving}
                disabled={!dirty || !isValid || isCreatingRxForm || !!status || !!saving}
                onClick={submitForm}
              >
                Submit
              </LoadingButton>
            )}
          />
        )}
      </LoadAvailableLocations>
      {!!saving && (
        <SavingProcess
          skipCustomBracketSetCreationRequest
          skipBondingDateReminder
          values={saving}
          submit={createRxForm}
          cancel={() => setSaving(null)}
          finish={() => navigate()}
        />
      )}
    </Card>
  );

  return withOrganizationPricing({ organizationId: organization.id, children });
};

AddRetainerRxForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  match: PropTypes.object
};

AddRetainerRxForm.defaultProps = {
  match: { params: {} }
};

export default AddRetainerRxForm;
